import React from 'react';
import { ArrowLeft, Search, Filter, Grid } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template18 = () => {
    const navigate = useNavigate();

    return (
        <div className="w-full min-h-screen bg-white font-sans">
            {/* Clean Minimal Header */}
            <header className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-100">
                <div className="w-full px-4 lg:px-8 py-3 lg:py-4 flex justify-between items-center">
                    <button
                        onClick={() => navigate('/TemplateThemes')}
                        className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-black transition-colors"
                    >
                        <ArrowLeft size={18} />
                        <span className="text-sm">Back</span>
                    </button>
                    <h1 className="text-xl lg:text-3xl font-bold text-black tracking-tight">FOCAL</h1>
                    <div className="flex items-center gap-3">
                        <Search className="text-gray-400 hover:text-black cursor-pointer transition-colors" size={20} />
                        <Grid className="text-gray-400 hover:text-black cursor-pointer transition-colors" size={20} />
                    </div>
                </div>
            </header>

            {/* Clean Hero Section */}
            <section className="bg-gradient-to-b from-blue-50 to-cyan-50 py-16 lg:py-24">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-4xl lg:text-7xl font-light text-black mb-6 tracking-tight">
                            Focus on what matters
                        </h2>
                        <p className="text-lg lg:text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
                            Clean design. Quality products. Exceptional experience. Everything you need, nothing you don't.
                        </p>
                        <button className="bg-black text-white px-8 lg:px-10 py-3 lg:py-4 text-sm lg:text-base hover:bg-gray-800 transition-colors font-medium">
                            Shop Collection
                        </button>
                    </div>
                </div>
            </section>

            {/* Product Categories - Clean Grid */}
            <section className="bg-white py-16 lg:py-20">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <h2 className="text-3xl lg:text-5xl font-light text-black mb-4 tracking-tight">Shop by Category</h2>
                        <div className="w-16 h-px bg-black mx-auto"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
                        {[
                            { name: "Essentials", image: "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=600&h=400&fit=crop" },
                            { name: "Premium", image: "https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=600&h=400&fit=crop" },
                            { name: "Limited", image: "https://images.unsplash.com/photo-1560472355-536de3962603?w=600&h=400&fit=crop" }
                        ].map((category, index) => (
                            <div key={index} className="group relative overflow-hidden bg-gray-50 hover:shadow-lg transition-all duration-500">
                                <div className="aspect-[4/3] overflow-hidden">
                                    <img
                                        src={category.image}
                                        alt={category.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div className="p-6 lg:p-8 text-center">
                                    <h3 className="text-xl lg:text-2xl font-light text-black mb-2">{category.name}</h3>
                                    <button className="text-sm text-gray-600 hover:text-black transition-colors border-b border-gray-300 hover:border-black pb-1">
                                        Explore →
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Products - Minimal Grid */}
            <section className="bg-gray-50 py-16 lg:py-20">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="flex justify-between items-end mb-12 lg:mb-16">
                        <h2 className="text-3xl lg:text-5xl font-light text-black tracking-tight">Featured</h2>
                        <div className="flex items-center gap-4">
                            <Filter className="text-gray-400 hover:text-black cursor-pointer transition-colors" size={20} />
                            <span className="text-sm text-gray-500">24 items</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map(id => (
                            <div key={id} className="group bg-white hover:shadow-lg transition-all duration-500">
                                <div className="aspect-[3/4] overflow-hidden bg-gray-100">
                                    <img
                                        src={`https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=400&h=500&fit=crop&q=80&auto=format&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&crop=entropy&cs=tinysrgb&fm=jpg&q=80&fit=max&ixlib=rb-4.0.3&ixid=eyJhcHBfaWQiOjF9&auto=format&s=${id * 21 + 500}`}
                                        alt={`Product ${id}`}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div className="p-4 lg:p-6">
                                    <h3 className="text-sm lg:text-base font-light text-black mb-1 tracking-wide">Essential Item {id}</h3>
                                    <p className="text-lg lg:text-xl font-light text-black">${(49 + id * 10)}</p>
                                    <button className="w-full mt-3 lg:mt-4 bg-gray-100 text-black py-2 lg:py-3 text-xs lg:text-sm hover:bg-black hover:text-white transition-all duration-300 font-medium">
                                        Add to Cart
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Brand Statement - Minimal */}
            <section className="bg-white py-16 lg:py-24">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-4xl lg:text-6xl font-light text-black mb-8 tracking-tight leading-tight">
                            Quality is our focus
                        </h2>
                        <p className="text-lg lg:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-12">
                            Every piece is carefully selected and crafted to meet our exacting standards. We believe in
                            doing fewer things better, focusing on what truly matters: exceptional quality and timeless design.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 mt-16">
                            {[
                                { title: "Quality", desc: "Premium materials and craftsmanship" },
                                { title: "Simplicity", desc: "Clean design focused on essentials" },
                                { title: "Performance", desc: "Built to last and exceed expectations" }
                            ].map((value, index) => (
                                <div key={index} className="text-center">
                                    <h3 className="text-xl lg:text-2xl font-light text-black mb-3">{value.title}</h3>
                                    <p className="text-sm lg:text-base text-gray-600 leading-relaxed">{value.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Newsletter - Clean */}
            <section className="bg-gradient-to-b from-blue-50 to-cyan-50 py-16 lg:py-20">
                <div className="container mx-auto px-4 lg:px-8 text-center">
                    <h2 className="text-3xl lg:text-5xl font-light text-black mb-6 tracking-tight">Stay focused</h2>
                    <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                        Get updates on new arrivals and exclusive collections
                    </p>
                    <div className="max-w-md mx-auto flex gap-2">
                        <input
                            type="email"
                            placeholder="Your email"
                            className="flex-1 px-4 py-3 border border-gray-200 focus:border-black focus:outline-none transition-colors"
                        />
                        <button className="bg-black text-white px-6 py-3 hover:bg-gray-800 transition-colors font-medium">
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>

            {/* Minimal Footer */}
            <footer className="bg-white py-12 lg:py-16 border-t border-gray-100">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="text-center">
                        <h3 className="text-2xl font-light mb-6 tracking-tight">FOCAL</h3>
                        <div className="flex justify-center gap-8 lg:gap-12 text-sm text-gray-500 mb-8">
                            <span className="hover:text-black cursor-pointer transition-colors">About</span>
                            <span className="hover:text-black cursor-pointer transition-colors">Contact</span>
                            <span className="hover:text-black cursor-pointer transition-colors">Privacy</span>
                            <span className="hover:text-black cursor-pointer transition-colors">Terms</span>
                        </div>
                        <p className="text-xs text-gray-400">&copy; 2025 Focal. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template18;